import { Home, Menu, X } from 'lucide-react'
import { useState } from 'react'
import { Link, Outlet } from 'react-router'
import { Button } from '#app/components/ui/button'
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	navigationMenuTriggerStyle,
} from '#app/components/ui/navigation-menu'
import { cn } from '#app/utils/misc'

const products = [
	{
		title: 'Voor Huiseigenaren',
		href: '/products/homeowners',
		description: 'Beheer je renovatie visueel, van planning tot oplevering.',
	},
	{
		title: 'Voor Professionals',
		href: '/products/professionals',
		description: 'Stroomlijn projecten en communicatie met klanten.',
	},
	{
		title: 'Voor Teams',
		href: '/products/teams',
		description: 'Werk samen aan grote renovatieprojecten.',
	},
]

const solutions = [
	{
		title: 'Visuele Documentatie',
		href: '/solutions/visual-documentation',
		description: "Leg elke fase vast met foto's, video's en 360° views.",
	},
	{
		title: 'Project Planning',
		href: '/solutions/planning',
		description: 'Plan en volg je renovatie in vier duidelijke fases.',
	},
	{
		title: 'Team Samenwerking',
		href: '/solutions/collaboration',
		description: 'Houd iedereen op de hoogte met real-time updates.',
	},
	{
		title: 'Budget Beheer',
		href: '/solutions/budget',
		description: 'Beheer kosten, offertes en facturen op één plek.',
	},
]

const resources = [
	{
		title: 'Blog',
		href: '/insights',
		description: 'Tips, trends en inspiratie voor je renovatie.',
	},
	{
		title: 'Handleidingen',
		href: '/guides',
		description: 'Stap-voor-stap guides voor succesvolle renovaties.',
	},
	{
		title: 'Klantenverhalen',
		href: '/stories',
		description: 'Inspirerende renovatieprojecten van onze gebruikers.',
	},
	{
		title: 'API Documentatie',
		href: '/developers',
		description: 'Integreer Woonplan in je eigen tools.',
	},
]

export default function MarketingLayout() {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

	return (
		<div className="light flex min-h-screen flex-col" data-theme="light">
			{/* Navigation Header */}
			<header className="bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50 w-full border-b backdrop-blur">
				<nav className="container flex h-16 items-center justify-between">
					{/* Logo */}
					<Link
						to="/"
						className="flex items-center gap-2 transition-opacity hover:opacity-80"
					>
						<div className="bg-primary text-primary-foreground flex h-9 w-9 items-center justify-center rounded-lg">
							<Home className="h-5 w-5" />
						</div>
						<div className="flex flex-col leading-none">
							<span className="text-sm font-light">woon</span>
							<span className="text-sm font-bold">plan</span>
						</div>
					</Link>

					{/* Desktop Navigation */}
					<NavigationMenu className="hidden lg:flex">
						<NavigationMenuList>
							{/* Products */}
							<NavigationMenuItem>
								<NavigationMenuTrigger>Producten</NavigationMenuTrigger>
								<NavigationMenuContent>
									<ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
										{products.map((product) => (
											<ListItem
												key={product.title}
												title={product.title}
												href={product.href}
											>
												{product.description}
											</ListItem>
										))}
									</ul>
								</NavigationMenuContent>
							</NavigationMenuItem>

							{/* Solutions */}
							<NavigationMenuItem>
								<NavigationMenuTrigger>Oplossingen</NavigationMenuTrigger>
								<NavigationMenuContent>
									<ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
										{solutions.map((solution) => (
											<ListItem
												key={solution.title}
												title={solution.title}
												href={solution.href}
											>
												{solution.description}
											</ListItem>
										))}
									</ul>
								</NavigationMenuContent>
							</NavigationMenuItem>

							{/* Pricing */}
							<NavigationMenuItem>
								<Link to="/pricing">
									<NavigationMenuLink className={navigationMenuTriggerStyle()}>
										Prijzen
									</NavigationMenuLink>
								</Link>
							</NavigationMenuItem>

							{/* Resources */}
							<NavigationMenuItem>
								<NavigationMenuTrigger>Resources</NavigationMenuTrigger>
								<NavigationMenuContent>
									<ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
										{resources.map((resource) => (
											<ListItem
												key={resource.title}
												title={resource.title}
												href={resource.href}
											>
												{resource.description}
											</ListItem>
										))}
									</ul>
								</NavigationMenuContent>
							</NavigationMenuItem>

							{/* Contact */}
							<NavigationMenuItem>
								<Link to="/contact">
									<NavigationMenuLink className={navigationMenuTriggerStyle()}>
										Contact
									</NavigationMenuLink>
								</Link>
							</NavigationMenuItem>
						</NavigationMenuList>
					</NavigationMenu>

					{/* CTA Buttons */}
					<div className="hidden items-center gap-4 lg:flex">
						<Button variant="ghost" asChild>
							<Link to="/login">Log In</Link>
						</Button>
						<Button asChild>
							<Link to="/start">Start Gratis</Link>
						</Button>
					</div>

					{/* Mobile Menu Toggle */}
					<button
						className="lg:hidden"
						onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
					>
						{mobileMenuOpen ? (
							<X className="h-6 w-6" />
						) : (
							<Menu className="h-6 w-6" />
						)}
					</button>
				</nav>

				{/* Mobile Menu */}
				{mobileMenuOpen && (
					<div className="border-t lg:hidden">
						<div className="container space-y-4 py-4">
							<div className="space-y-2">
								<h3 className="text-sm font-semibold">Producten</h3>
								{products.map((product) => (
									<Link
										key={product.href}
										to={product.href}
										className="text-muted-foreground hover:text-foreground block py-2 text-sm"
										onClick={() => setMobileMenuOpen(false)}
									>
										{product.title}
									</Link>
								))}
							</div>

							<div className="space-y-2">
								<h3 className="text-sm font-semibold">Oplossingen</h3>
								{solutions.map((solution) => (
									<Link
										key={solution.href}
										to={solution.href}
										className="text-muted-foreground hover:text-foreground block py-2 text-sm"
										onClick={() => setMobileMenuOpen(false)}
									>
										{solution.title}
									</Link>
								))}
							</div>

							<div className="space-y-2">
								<Link
									to="/pricing"
									className="block py-2 font-semibold"
									onClick={() => setMobileMenuOpen(false)}
								>
									Prijzen
								</Link>
								<Link
									to="/insights"
									className="block py-2 font-semibold"
									onClick={() => setMobileMenuOpen(false)}
								>
									Insights
								</Link>
								<Link
									to="/contact"
									className="block py-2 font-semibold"
									onClick={() => setMobileMenuOpen(false)}
								>
									Contact
								</Link>
							</div>

							<div className="flex flex-col gap-2 border-t pt-4">
								<Button variant="outline" asChild className="w-full">
									<Link to="/login" onClick={() => setMobileMenuOpen(false)}>
										Log In
									</Link>
								</Button>
								<Button asChild className="w-full">
									<Link to="/start" onClick={() => setMobileMenuOpen(false)}>
										Start Gratis
									</Link>
								</Button>
							</div>
						</div>
					</div>
				)}
			</header>

			{/* Page Content */}
			<main className="flex-1">
				<Outlet />
			</main>

			{/* Footer */}
			<footer className="bg-muted/50 border-t">
				<div className="container py-12">
					<div className="grid grid-cols-2 gap-8 md:grid-cols-4">
						{/* Company */}
						<div>
							<h3 className="mb-3 font-semibold">Bedrijf</h3>
							<ul className="text-muted-foreground space-y-2 text-sm">
								<li>
									<Link to="/about" className="hover:text-foreground">
										Over ons
									</Link>
								</li>
								<li>
									<Link to="/careers" className="hover:text-foreground">
										Werken bij
									</Link>
								</li>
								<li>
									<Link to="/press" className="hover:text-foreground">
										Pers
									</Link>
								</li>
								<li>
									<Link to="/partners" className="hover:text-foreground">
										Partners
									</Link>
								</li>
							</ul>
						</div>

						{/* Product */}
						<div>
							<h3 className="mb-3 font-semibold">Product</h3>
							<ul className="text-muted-foreground space-y-2 text-sm">
								<li>
									<Link to="/features" className="hover:text-foreground">
										Features
									</Link>
								</li>
								<li>
									<Link to="/pricing" className="hover:text-foreground">
										Prijzen
									</Link>
								</li>
								<li>
									<Link to="/security" className="hover:text-foreground">
										Beveiliging
									</Link>
								</li>
								<li>
									<Link to="/roadmap" className="hover:text-foreground">
										Roadmap
									</Link>
								</li>
							</ul>
						</div>

						{/* Support */}
						<div>
							<h3 className="mb-3 font-semibold">Support</h3>
							<ul className="text-muted-foreground space-y-2 text-sm">
								<li>
									<Link to="/support" className="hover:text-foreground">
										Help Center
									</Link>
								</li>
								<li>
									<Link to="/guides" className="hover:text-foreground">
										Handleidingen
									</Link>
								</li>
								<li>
									<Link to="/api" className="hover:text-foreground">
										API Docs
									</Link>
								</li>
								<li>
									<Link to="/status" className="hover:text-foreground">
										Status
									</Link>
								</li>
							</ul>
						</div>

						{/* Legal */}
						<div>
							<h3 className="mb-3 font-semibold">Juridisch</h3>
							<ul className="text-muted-foreground space-y-2 text-sm">
								<li>
									<Link to="/privacy" className="hover:text-foreground">
										Privacy
									</Link>
								</li>
								<li>
									<Link to="/tos" className="hover:text-foreground">
										Voorwaarden
									</Link>
								</li>
								<li>
									<Link to="/cookies" className="hover:text-foreground">
										Cookies
									</Link>
								</li>
								<li>
									<Link to="/gdpr" className="hover:text-foreground">
										GDPR
									</Link>
								</li>
							</ul>
						</div>
					</div>

					<div className="mt-8 flex flex-col items-center justify-between gap-4 border-t pt-8 md:flex-row">
						<div className="flex items-center gap-2">
							<Home className="text-muted-foreground h-5 w-5" />
							<span className="text-muted-foreground text-sm">
								© 2025 Woonplan. Alle rechten voorbehouden.
							</span>
						</div>
						<div className="flex gap-4">
							<Link
								to="/nl"
								className="text-muted-foreground hover:text-foreground text-sm"
							>
								🇳🇱 Nederlands
							</Link>
							<Link
								to="/en"
								className="text-muted-foreground hover:text-foreground text-sm"
							>
								🇬🇧 English
							</Link>
						</div>
					</div>
				</div>
			</footer>
		</div>
	)
}

const ListItem = ({ className, title, children, href, ...props }: any) => {
	return (
		<li>
			<NavigationMenuLink asChild>
				<Link
					to={href}
					className={cn(
						'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block space-y-1 rounded-md p-3 leading-none no-underline transition-colors outline-none select-none',
						className,
					)}
					{...props}
				>
					<div className="text-sm leading-none font-medium">{title}</div>
					<p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
						{children}
					</p>
				</Link>
			</NavigationMenuLink>
		</li>
	)
}
